'use client';

import type { RuleOverheadProject } from '@/app/models/ruleOverheadProject';
import { useRemoveOverHeadProjectRuleMutation } from '@/app/services/ruleOverHeadProjectApi';
import ActionMenu, { ItemAction } from '@/components/common/ActionMenu';
import DataTable, { bodyCellSx, Column } from '@/components/common/DataTable';
import CancelOrConfirmDialog from '@/components/common/dialogs/CancelOrConfirmDialog';
import { TableCell, useTheme } from '@mui/material';
import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

type EventTableParams = {
  rows: RuleOverheadProject[];
  setSelectedItem: (p: RuleOverheadProject) => void;
  setDrawerOpen: (open: boolean) => void;
};

export default function AllocationPriorityTable(props: EventTableParams) {
  const theme = useTheme();
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [page, setPage] = useState(0);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [deleteDialog, setDeleteDialog] = useState<boolean>(false);

  const { t } = useTranslation(['allocationPriority', 'common']);
  const { rows, setSelectedItem, setDrawerOpen } = props;

  const [deleteClientPriority] = useRemoveOverHeadProjectRuleMutation();

  const handleDialogClose = () => {
    setDeleteDialog(false);
  };

  const handleDeleteItem = async () => {
    if (selectedId) {
      const response = await deleteClientPriority(selectedId);
      if ('data' in response) {
        toast.dismiss();
        toast.success(
          t('delete.rule.project.priority.success', {
            position: toast.POSITION.TOP_RIGHT,
          }),
        );
      } else {
        toast.error(
          t('common:processing.failed', {
            position: toast.POSITION.TOP_RIGHT,
          }),
        );
      }
      handleDialogClose();
      setSelectedId(null);
    }
  };

  const columns: Column[] = [
    { key: 'project', label: t('common:project'), width: theme.spacing(80) },
    { key: 'priority', label: t('common:priority'), align: 'center' },
    { key: 'actions', label: t('common:actions'), align: 'right' },
  ];

  const renderRow = (row: RuleOverheadProject) => (
    <>
      <TableCell sx={bodyCellSx}>{`${row.project.code} - ${row.project.name}`}</TableCell>
      <TableCell sx={{ ...bodyCellSx, textAlign: 'center' }}>{`${row.priority}`}</TableCell>
      <TableCell sx={{ ...bodyCellSx, textAlign: 'right' }}>
        <ActionMenu>
          <ItemAction
            text={t('common:edit') as string}
            actionType="edit"
            onClick={() => {
              setSelectedItem(row);
              setDrawerOpen(true);
            }}
          />
          <ItemAction
            text={t('common:delete') as string}
            actionType="delete"
            onClick={() => {
              setSelectedId(row.id);
              setDeleteDialog(true);
            }}
          />
        </ActionMenu>
      </TableCell>
    </>
  );

  // Use the row ID as the unique key
  const getRowKey = (row: RuleOverheadProject) => row.id;

  const paginationConfig = {
    page,
    rowsPerPage,
    totalElements: rows?.length || 0,
    rowsPerPageOptions: [25, 50, 100, 200],
    onPageChange: setPage,
    onRowsPerPageChange: setRowsPerPage,
    labelDisplayedRows: ({ from, to, count }: { from: number; to: number; count: number }) =>
      `${from}-${to} ${t('de')} ${count}`,
    labelRowsPerPage: t('common:itens.per.page'),
  };

  const deleteDialogProps = {
    title: t('confirm.delete.title'),
    contentText: <Trans>{t('confirm.delete.message')}</Trans>,
  };

  return (
    <>
      <DataTable
        columns={columns}
        data={rows}
        renderRow={renderRow}
        getRowKey={getRowKey}
        emptyMessage={t('common:no.data.for.the.chosen.period.and.search.term')}
        pagination={paginationConfig}
        stickyHeader
        size="small"
      />

      <CancelOrConfirmDialog
        title={deleteDialogProps.title}
        contentText={deleteDialogProps.contentText}
        open={deleteDialog}
        onClose={() => handleDialogClose()}
        onConfirm={async () => handleDeleteItem()}
      />
    </>
  );
}
